import type { CellState, CellValue } from "@quovy/types";

/**
 * Discriminated union over the mutually-exclusive *body* states a cell can
 * be in. This is intentionally separate from the cell's *overlay* state
 * (locked / needs-review) — those layer on top of any body kind
 * and live in `CellOverlays`.
 *
 * Body-state precedence (highest first):
 *   1. editing  — caller flagged the cell as in inline-edit mode
 *   2. queued / running -> `loading`
 *   3. failed -> `error`
 *   4. completed && !hasValue -> `not-found`
 *   5. cleared OR (no run yet && !hasValue) -> `empty`
 *   6. otherwise -> `value`
 *
 * Note: `empty` ("never ran" or "explicitly cleared") and `not-found`
 * ("we ran and got nothing") are distinct on purpose — the renderers
 * surface different affordances.
 */
export type CellView =
  | { kind: "empty" }
  | { kind: "not-found" }
  | { kind: "loading"; status: "queued" | "running" }
  | { kind: "error"; errorMessage: string; attempt?: number }
  | { kind: "value"; value: CellValue }
  | { kind: "editing"; value: CellValue };

/**
 * Cell overlays — independent of body state. Compose alongside, not inside.
 *
 * Override precedence (highest first):
 *   locked > needs-review
 *
 * Only one badge is rendered at a time; this precedence is the contract
 * tested in `grid-cell.test.tsx`.
 */
export interface CellOverlays {
  badge: "locked" | "needs-review" | null;
}

function cellHasValue(value: CellValue): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/**
 * Pure selector mapping a `CellState` (plus the caller's `editing`
 * flag) to the body-view union. No JSX, no React — testable in isolation.
 */
export function deriveCellView(cell: CellState, editing?: boolean): CellView {
  if (editing) {
    return { kind: "editing", value: cell.value };
  }

  const status = cell.cellRun?.status;
  const hasValue = cellHasValue(cell.value);

  if (status === "queued" || status === "running") {
    return { kind: "loading", status };
  }

  if (status === "failed") {
    const view: CellView = {
      kind: "error",
      errorMessage: cell.cellRun?.errorMessage ?? "Cell run failed.",
    };
    if (cell.cellRun?.attempt !== undefined) {
      (view as { attempt?: number }).attempt = cell.cellRun.attempt;
    }
    return view;
  }

  if (status === "completed" && !hasValue) {
    return { kind: "not-found" };
  }

  if (status === "cleared" || (!status && !hasValue)) {
    return { kind: "empty" };
  }

  // Have a value (or a non-empty value with no run status).
  if (hasValue) {
    return { kind: "value", value: cell.value };
  }

  // Defensive: any leftover combination (e.g. status === "completed" *with*
  // a value) is captured by the `hasValue` branch above; if we somehow
  // reach here, render as empty rather than crash.
  return { kind: "empty" };
}

/**
 * Pure selector for the cell-overlay state. See `CellOverlays` for the
 * precedence contract.
 */
export function deriveCellOverlays(cell: CellState): CellOverlays {
  if (cell.locked) return { badge: "locked" };
  if (cell.needsReview) return { badge: "needs-review" };
  return { badge: null };
}
